import React, { useEffect, useState } from 'react'
import { useDisclosure } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import axiosInstance from 'api/axiosInstance'
import { useAuth } from 'contexts/AuthContext'
import WhatsNewModal from './WhatsNewModal'
import whatsNewAnnouncements from './announcements'

const SEEN_KEY_PREFIX = 'whats-new-seen'

const buildSeenKey = (userId, announcementId) => `${SEEN_KEY_PREFIX}:${userId}:${announcementId}`

const getNextAnnouncement = userId =>
    whatsNewAnnouncements.find(announcement =>
        announcement.isActive !== false && !localStorage.getItem(buildSeenKey(userId, announcement.id))
    )

const resolveAnnouncement = async announcement => {
    if (!announcement?.ctaPathResolverEndpoint) {
        return announcement
    }

    const fallbackCtaPath = announcement.fallbackCtaPath || announcement.ctaPath || '/admin/products'

    try {
        const { data } = await axiosInstance.get(announcement.ctaPathResolverEndpoint)
        const dashboardUrl = typeof data?.dashboard_url === 'string' && data.dashboard_url.trim()
            ? data.dashboard_url.trim()
            : fallbackCtaPath
        const productName = typeof data?.product_name === 'string' ? data.product_name.trim() : ''

        return {
            ...announcement,
            ctaPath: dashboardUrl,
            description: productName
                ? `${announcement.description} Start with ${productName}.`
                : announcement.description,
        }
    } catch (_) {
        return {
            ...announcement,
            ctaPath: fallbackCtaPath,
        }
    }
}

const WhatsNewAnnouncements = () => {
    const { user, loading } = useAuth()
    const navigate = useNavigate()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [currentAnnouncement, setCurrentAnnouncement] = useState(null)

    useEffect(() => {
        let isMounted = true

        const openAnnouncement = async () => {
            if (loading || !user?.id || user?.is_employee) {
                return
            }

            const nextAnnouncement = getNextAnnouncement(user.id)

            if (!nextAnnouncement) {
                return
            }

            const resolvedAnnouncement = await resolveAnnouncement(nextAnnouncement)

            if (!isMounted) {
                return
            }

            setCurrentAnnouncement(resolvedAnnouncement)
            onOpen()
        }

        openAnnouncement()

        return () => {
            isMounted = false
        }
    }, [loading, onOpen, user])

    const markCurrentAnnouncementAsSeen = () => {
        if (!user?.id || !currentAnnouncement) {
            return
        }

        localStorage.setItem(buildSeenKey(user.id, currentAnnouncement.id), 'true')
    }

    const handleClose = () => {
        markCurrentAnnouncementAsSeen()
        onClose()
        setCurrentAnnouncement(null)
    }

    const handlePrimaryAction = () => {
        if (!currentAnnouncement?.ctaPath) {
            handleClose()
            return
        }

        markCurrentAnnouncementAsSeen()
        onClose()
        navigate(currentAnnouncement.ctaPath)
        setCurrentAnnouncement(null)
    }

    return (
        <WhatsNewModal
            announcement={currentAnnouncement}
            isOpen={isOpen}
            onClose={handleClose}
            onPrimaryAction={handlePrimaryAction}
        />
    )
}

export default WhatsNewAnnouncements
